<template>
  <a-modal
    v-model="showModal"
    title="Add affiliate"
    :footer="null"
    width="500px"
    :destroyOnClose="true"
    class="approve-cashback-modal"
  >
    <div class="mb-3">
      <a-radio-group v-model="mode" button-style="solid" class="mb-4">
        <a-radio-button value="single">Add user</a-radio-button>
        <a-radio-button value="bulk">Import bulk of users</a-radio-button>
      </a-radio-group>

      <div v-if="mode === 'single'">
        <a-form-item
          label="Login"
          required
          prop="login"
          :colon="false"
          :rules="[{ required: true, message: 'Login is required', trigger: ['blur', 'change'] }]"
        >
          <a-input
            v-model="login"
            placeholder="Login"
            size="large"
            allow-clear
            :disabled="loading"
          />
        </a-form-item>
      </div>

      <div v-else>
        <a-form-model-item
          label="Image"
          prop="image"
          :colon="false"
          class="w-full mr-5"
        >
          <a-upload-dragger
            name="file"
            action="#"
            class="upload-area"
            accept=".csv"
            :multiple="false"
            :show-upload-list="false"
            :disabled="loading || imageLoading"
            :before-upload="beforeUpload"
            @change="fileChange"
          >
            <p class="ant-upload-drag-icon">
              <a-icon type="inbox" />
            </p>
            <p class="ant-upload-text">Click or drag .csv file to this area to upload</p>
            <p class="ant-upload-hint">Maximum file size: 40Kb</p>
          </a-upload-dragger>
          <div v-if="uploadedFileName" class="mt-2">
            Uploaded: <span class="text-success">{{ uploadedFileName }}</span>
          </div>
        </a-form-model-item>
        <div class="d-flex align-items-center">
          <div @click="downloadExampleFile" class="d-flex align-items-center cursor-pointer">
            <a-icon type="download" class="mr-2 text-primary font-size-16" />
            <span class="font-size-16 text-primary">Download file example</span>
          </div>
          <a-tooltip
            placement="top"
            arrow-point-at-center
            :get-popup-container="getPopupContainer"
          >
            <template slot="title">
              <span>Login is required, phone is optional. File structure must match the example</span>
            </template>
            <a-icon class="d-flex ml-2 font-size-16" type="info-circle" />
          </a-tooltip>
        </div>
      </div>
    </div>
    <a-row class="footer-viewscreen footer d-flex justify-content-end">
      <a-button
        :loading="loading"
        @click="closeModal"
      >
        Cancel
      </a-button>
      <a-button
        type="primary"
        class="ml-2"
        :loading="loading || imageLoading"
        @click="addAffiliate"
      >
        Save
      </a-button>
    </a-row>
  </a-modal>
</template>

<script>
import apiClient from '@/services/axios'
import { beforeUpload } from '@/composables/useUploadImage.js'
import { addSingleMember } from '@/views/apps/referral-program/referral-programs/members/composables/useAddMember.js'
import { getErrorMessage } from '@/views/apps/fortune-wheel/composables/useGetErrorMessage.js'
import { subscribe, unsubscribe } from '@/composables/keydownEvent.js'

const referralApiUrl = process.env.VUE_APP_REFERRAl_API_URL

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    roleSystemName: {
      type: String,
      default: 'affiliate',
    },
  },
  components: {
  },
  data: () => ({
    mode: 'single',
    login: '',
    file: null,
    uploadedFileName: '',
    showModal: false,
    imageLoading: false,
    loading: false,
  }),
  watch: {
    show: {
      handler(val) {
        if (val) {
          this.showModal = true
        } else {
          this.showModal = false
        }
      },
      deep: true,
      immediate: true,
    },
    showModal: {
      handler(val) {
        if (!val) {
          this.closeModal()
        }
      },
      deep: true,
    },
  },
  methods: {
    getPopupContainer() {
      return document.body
    },
    async beforeUpload(file) {
      return await beforeUpload(this, file, 0.4, null, ['text/csv'])
    },
    fileChange(info) {
      if (info.file.status === 'uploading') {
        this.imageLoading = true
        return
      }

      info.file.status = 'done'

      this.file = info.file.originFileObj
      this.uploadedFileName = info.file.name
      this.imageLoading = false
    },
    downloadExampleFile() {
      const fileUrl = '/resources/examples/ref-example.csv'
      const link = document.createElement('a')
      link.href = fileUrl
      link.download = 'ref-example.csv'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    addAffiliate() {
      if (this.mode === 'single') {
        this.addSingle()
        return
      }
      this.addBulk()
    },
    async addSingle() {
      const programId = this.$route.params.id
      const data = await addSingleMember(this, programId, this.roleSystemName, { login: this.login })
      if (data) {
        this.$notification.success({
          message: 'User successfully added',
        })
        this.$emit('updateTable')
        this.closeModal()
      }
    },
    async addBulk() {
      try {
        this.loading = true
        const programId = this.$route.params.id

        const formData = new FormData()
        formData.append('members', this.file)

        const url = `${referralApiUrl}/referral-programs/${programId}/members/${this.roleSystemName}/add-bulk`
        const response = await apiClient.post(url, formData)
        if (response?.data?.data) {
          this.$notification.success({
            message: 'Users successfully added',
          })
          this.$emit('updateTable')
          this.closeModal()
          return
        }
        getErrorMessage(response?.data)
      } catch (e) {
        this.$notification.error({
          message: e?.message ? e.message : 'Error',
        })
      } finally {
        this.loading = false
      }
    },
    closeModal() {
      this.$emit('close')
      setTimeout(() => {
        this.login = ''
        this.file = null
        this.uploadedFileName = ''
      }, 500)
    },
  },
  mounted() {
    subscribe(this.addAffiliate)
  },
  beforeDestroy() {
    unsubscribe()
  },
}
</script>

<style lang="scss">
.approve-cashback-modal {
  .ant-input-number {
    width: 100%;
  }

  .ant-input-number-handler-wrap {
    display: none !important;
  }

  .footer {
    display: flex;
    justify-content: space-between;
    &::before,
    &::after {
      content: none;
    }
  }
}
</style>
